import type { SafetyReportDetail } from "@wallyo/contracts/console/safety";
import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { getSafetyReport, listSafetyReports } from "../../api/console-api";
import { useApi } from "../../api/context";
import { ConsoleApiError } from "../../api/errors";
import { createIntent } from "../../api/idempotency";
import { t } from "../../i18n";
import { useHash } from "../../navigation/use-hash";
import { useAlerts } from "../../shell/alerts";
import { ErrorNotice } from "../../shell/ErrorNotice";
import { PageTop } from "../../shell/PageTop";
import { useStaff } from "../../shell/staff-context";
import { useNow } from "../cases/use-now";
import { actionProblem, emptyFields, reportActions, sendReportAction, type ReportActionKind } from "./report-actions";
import { describeReportFailure } from "./report-format";
import { parseReportsHash, reportsHref, reportsRequest, type ReportFilters } from "./report-route";
import { ReportActionDialog, type ReportDialogState } from "./ReportActionDialog";
import { ReportDetailView } from "./ReportDetailView";
import { NewReportDialog } from "./NewReportDialog";
import { ReportListView, type ReportListState } from "./ReportListView";

/** The overdue pills follow the clock while the screen is open. */
const TICK_MS = 30_000;

/**
 * Signalements (HANDLE_REPORTS, checked by the shell). `#reports` is the list,
 * `#reports/<id>` opens one beside it. Every write names the version on screen
 * with one Idempotency-Key per intent; the screen changes only from the
 * server's answer (ADR-0016), then the list is read again.
 */
const HANDLE_REPORTS = "HANDLE_REPORTS_STAFF_FEATURE_ENABLEMENT";
const SEARCH_ACCOUNTS = "SEARCH_ACCOUNTS_STAFF_FEATURE_ENABLEMENT";

export function ReportsScreen() {
  const hash = useHash();
  const { client } = useApi();
  const { me } = useStaff();
  // Opening a report is gated on the same feature as handling one
  // (OPEN_SAFETY_REPORT_STAFF_ACTION). The server checks again; this only
  // decides whether to offer the button (WALLIYO-0122).
  const canOpen = me.features.includes(HANDLE_REPORTS);
  const [newReport, setNewReport] = useState(false);
  const { filters, safetyReportId } = parseReportsHash(hash);
  const now = useNow(TICK_MS);
  const [list, setList] = useState<ReportListState>({ status: "loading", reports: [] });
  const [reads, readList] = useReducer((count: number) => count + 1, 0);
  const filtersKey = reportsHref(filters);

  useEffect(() => {
    let current = true;
    setList((was) => ({ status: "loading", reports: was.reports }));
    listSafetyReports(client, reportsRequest(parseReportsHash(filtersKey).filters)).then(
      ({ reports }) => current && setList({ status: "ready", reports }),
      (error: unknown) => current && setList((was) => ({ status: "error", reports: was.reports, error })),
    );
    return () => {
      current = false;
    };
  }, [client, filtersKey, reads]);

  const onFilters = useCallback(
    (next: ReportFilters) => {
      window.location.hash = reportsHref(next, safetyReportId);
    },
    [safetyReportId],
  );

  return (
    <>
      <PageTop title={t("screen.reports")} sub={t("sub.reports")} />
      {/* A complaint that arrived by phone: the app has a report button, the
          console did not, so there was nowhere to record one (WALLIYO-0122). */}
      {newReport ? (
        <NewReportDialog
          canSearch={me.features.includes(SEARCH_ACCOUNTS)}
          onClose={() => setNewReport(false)}
          onOpened={(report) => {
            setNewReport(false);
            readList();
            window.location.hash = reportsHref(filters, report.safetyReportId);
          }}
        />
      ) : null}
      <div className="split list">
        <ReportListView
          filters={filters}
          state={list}
          selectedId={safetyReportId}
          onFilters={onFilters}
          onRetry={readList}
          onNewReport={canOpen ? () => setNewReport(true) : null}
        />
        {safetyReportId ? (
          <ReportPanel
            key={safetyReportId}
            safetyReportId={safetyReportId}
            filters={filters}
            now={now}
            onChanged={readList}
          />
        ) : (
          <div className="card">
            <p className="foot">{t("reports.pick")}</p>
          </div>
        )}
      </div>
    </>
  );
}

type PanelState =
  | { status: "loading"; detail: SafetyReportDetail | null }
  | { status: "ready"; detail: SafetyReportDetail }
  | { status: "failed"; detail: SafetyReportDetail | null; error: unknown };

function ReportPanel({
  safetyReportId,
  filters,
  now,
  onChanged,
}: {
  safetyReportId: string;
  filters: ReportFilters;
  now: Date;
  onChanged: () => void;
}) {
  const { client } = useApi();
  const { me } = useStaff();
  const [state, setState] = useState<PanelState>({ status: "loading", detail: null });
  const [reads, read] = useReducer((count: number) => count + 1, 0);
  const [dialog, setDialog] = useState<ReportDialogState | null>(null);
  const alerts = useAlerts();
  const intent = useRef(createIntent());
  const inFlight = useRef(false);

  useEffect(() => {
    let current = true;
    setState((was) => ({ status: "loading", detail: was.detail }));
    getSafetyReport(client, safetyReportId).then(
      ({ safetyReport }) => current && setState({ status: "ready", detail: safetyReport }),
      (error: unknown) => current && setState((was) => ({ status: "failed", detail: was.detail, error })),
    );
    return () => {
      current = false;
    };
  }, [client, safetyReportId, reads]);

  const detail = state.detail;

  const reload = useCallback(() => {
    intent.current.reset();
    setDialog(null);
    read();
    onChanged();
  }, [onChanged]);

  if (!detail) {
    return (
      <div className="card">
        {state.status === "failed" ? (
          <div className="audit-error">
            <p className="foot">
              {state.error instanceof ConsoleApiError && state.error.status === 404
                ? t("reports.notFound")
                : t("reports.loadFailed")}
            </p>
            <ErrorNotice error={state.error} />
            <div>
              <button className="btn primary" type="button" onClick={read}>
                {t("audit.retry")}
              </button>
            </div>
          </div>
        ) : (
          <div className="empty" role="status">
            <span className="spinner" aria-hidden="true" />
            <span>{t("reports.loading")}</span>
          </div>
        )}
      </div>
    );
  }

  const open = (kind: ReportActionKind) => {
    intent.current.reset();
    setDialog({ kind, fields: emptyFields(detail), problem: null, failure: null, pending: false });
  };

  const submit = async () => {
    if (!dialog || inFlight.current) return;
    const problem = actionProblem(dialog.kind, dialog.fields, new Date());
    if (problem) {
      setDialog({ ...dialog, problem });
      return;
    }
    inFlight.current = true;
    setDialog({ ...dialog, problem: null, failure: null, pending: true });
    try {
      const { safetyReport } = await sendReportAction(client, detail, dialog.kind, dialog.fields, intent.current.key());
      // Never optimistic: the report shows as the server answered it.
      intent.current.reset();
      setDialog(null);
      setState({ status: "ready", detail: safetyReport });
      alerts.ok(t(`reports.done.${dialog.kind}` as const, { reference: safetyReport.reference }));
      onChanged();
    } catch (error) {
      const context = dialog.kind === "appeal" ? "appeal" : dialog.kind === "share" ? "share" : "general";
      setDialog((was) => was && { ...was, pending: false, failure: describeReportFailure(error, context) });
    } finally {
      inFlight.current = false;
    }
  };

  return (
    <div className="case-column">
      {state.status === "failed" ? (
        <div className="banner bad" role="alert">
          <span>{t("reports.refreshFailed")}</span>
          <button className="btn" type="button" onClick={read}>
            {t("audit.retry")}
          </button>
        </div>
      ) : null}
      <ReportDetailView
        detail={detail}
        filters={filters}
        actions={reportActions(detail, { staffMemberId: me.staffMember.staffMemberId, features: me.features }, now)}
        now={now}
        onAction={open}
      />
      {dialog ? (
        <ReportActionDialog
          detail={detail}
          dialog={dialog}
          onFields={(fields) => {
            // Anything edited is a new intent.
            intent.current.reset();
            setDialog((was) => was && { ...was, fields: { ...was.fields, ...fields }, problem: null });
          }}
          onSubmit={() => void submit()}
          onClose={() => setDialog(null)}
          onReload={reload}
        />
      ) : null}
    </div>
  );
}
